import {
	observable,
	computed,
	action,
	IReactionDisposer,
} from "mobx";
import { DateTime } from "luxon";

import { FormattedLookup } from "@data-types/my-bets/Lookups";
import { FormFilter } from "@data-types/my-bets/FormFilter";
import {
	getUrlParamsAsObj,
	redirectPreserveSearchParams,
	updateUrlParams,
} from "@state/utils";
import { BetStatus, BetPeriod, getCurrentCulture } from "@utils";
import { UserAccountType } from "@common/constants";
import { RootStore } from "../types";
import MyBetsStore from "./MyBetsStore";
import { Filter, rawFilter } from "@api-types/my-bets/Filter";
import { logger } from "@utils/logger/logger";
import { StorageStateKeysEnum } from "@utils/storage";
import { safeJsonParse } from "@utils/format/formatter";
import {
	getShopIdAndBettingTypeAbrv,
	mapBettingTypeAbrvToId,
} from "@utils/common/bettingAccountUtils";

function createDefaultRawData(): rawFilter {
	return {
		bettingAccountTypeAbrv: null,
		betStatusAbrv: null,

		period: BetPeriod.all,
		from: null,
		to: null,

		isLive: null,
		fetchReport: false,
		pageNumber: 1,
	};
}

export class BaseMyBetsFilterStore {
	rootStore: RootStore;
	myBetsStore: MyBetsStore;
	urlParamsUpdatedReaction: IReactionDisposer | null;
	eventId: string | null;

	_activeTab: BetStatus = BetStatus.OPEN;

	constructor(rootStore: RootStore, myBetsStore: MyBetsStore) {
		this.rootStore = rootStore;
		this.myBetsStore = myBetsStore;

		this.setDefaultFilterData();

		this.getUrlData = this.getUrlData.bind(this);
	}

	@observable isInitialized = false;

	@observable rawData: rawFilter = {
		bettingAccountTypeAbrv: null,
		betStatusAbrv: null,

		period: null,
		from: null,
		to: null,

		isLive: null,
		shopId: null,

		pageNumber: 1,
	};

	@computed get filter() {
		const filter: Filter = {};

		if (this.embed != null) {
			filter.embed = this.embed;
		}

		if (this.betSlipStatusIds != null) {
			filter.betSlipStatusIds = this.betSlipStatusIds;
		}

		if (this.mapToBetStatusIds != null) {
			filter.betStatusIds = this.mapToBetStatusIds;
		}

		if (this.mapBettingTypeAbrvToId?.length) {
			filter.bettingAccountTypeIds = this.mapBettingTypeAbrvToId;
		}

		if (this.dateRange != null) {
			filter.from = this.dateRange.from;
			filter.to = this.dateRange.to;
		}

		if (this.isLive != null) {
			filter.isLive = this.isLive;
		}

		if (this.pageNumber != null) {
			filter.pageNumber = this.pageNumber;
		}

		return filter;
	}

	//#region filter components

	@computed get betSlipStatusIds() {
		return [
			this.myBetsStore.betSlipStatusLookups?.find(
				(s: FormattedLookup) => s.abrv === "submitted"
			)?.id,
		];
	}

	@computed get isDateRangeFalsy() {
		if (this.rawData.from && this.rawData.to) {
			const parsedFromValue = DateTime.fromISO(this.rawData.from);
			const parsedToValue = DateTime.fromISO(this.rawData.to);

			const isFromInvalid = !parsedFromValue.isValid;
			const isToInvalid = !parsedToValue.isValid;

			if (isFromInvalid || isToInvalid) {
				logger.logError("Invalid Date Range Detected", {
					extra: {
						from: this.rawData.from,
						to: this.rawData.to,
						parsedFrom: {
							isValid: parsedFromValue.isValid,
							invalidReason: parsedFromValue.invalidReason,
							explained: parsedFromValue.invalidExplanation,
						},
						parsedTo: {
							isValid: parsedToValue.isValid,
							invalidReason: parsedToValue.invalidReason,
							explained: parsedToValue.invalidExplanation,
						},
					},
					tags: {
						section: "date-range-validation",
						component:
							"DrawerFilterFormCustomPeriodCore / BaseMyBetsFilterStore",
					},
				});
				return true;
			}

			return false;
		}

		return false;
	}

	@computed get mapToBetStatusIds(): FormFilter["betStatusIds"] {
		if (this.rawData?.betStatusAbrv == null) {
			return null;
		}

		switch (this.rawData.betStatusAbrv) {
			case BetStatus.ALL:
				return null;
			case BetStatus.OPEN:
				const openStatus = this.myBetsStore.betStatusLookups?.find(
					(s: FormattedLookup) => s.abrv === BetStatus.OPEN
				)?.id;
				if (openStatus) {
					return [openStatus];
				}
				return null;
			case BetStatus.CLOSED:
				if (!this.myBetsStore.closedBetsBetStatuses?.length)
					return null;

				return this.myBetsStore.closedBetsBetStatuses.map(
					(s: FormattedLookup) => s.id
				);
			default:
				const status = this.myBetsStore.betStatusLookups.find(
					(lookup) => lookup.abrv == this.rawData.betStatusAbrv
				);

				if (status) {
					return [status.id];
				}
				return null;
		}
	}
	@computed get shopId() {
		if (
			this.rawData.bettingAccountTypeAbrv ==
			UserAccountType.SportBettingAccountOnline
		) {
			return this.rootStore.userAuthStore.defaultOnlineShopId;
		}
		const doesExists = this.rootStore.userAuthStore.shopUserShops.get(
			this.rawData.shopId || ""
		);
		if (doesExists) {
			return this.rawData.shopId;
		}
		return null;
	}
	@computed get mapBettingTypeAbrvToId(): string[] | null {
		return mapBettingTypeAbrvToId({
			bettingAccountTypeAbrv: this.rawData?.bettingAccountTypeAbrv,
			shopId: this.rawData?.shopId,
			rootStore: this.rootStore,
		});
	}

	@computed get dateRange() {
		if (!this.rawData.period || this.rawData.period === BetPeriod.all) {
			return null;
		}

		if (this.rawData.period !== BetPeriod.custom) {
			const timeInterval = this.myBetsStore.betPeriods
				.find((p: { value: string }) => p.value === this.rawData.period)
				?.getTimeInterval();
			if (timeInterval == null) {
				// Return default
				return this.myBetsStore.betPeriods[0].getTimeInterval();
			}
			return timeInterval;
		}

		if (!this.rawData.from || !this.rawData.to) {
			return null;
		}

		return {
			from: this.rawData.from,
			to: DateTime.fromISO(this.rawData.to)
				.endOf("day")
				.toUTC()
				.toString(),
		};
	}

	@computed get isLive() {
		// Only add isLive to filter if it is true;
		// If isLive=false is sent it will fetch bets that are not live and this check
		// Servers as option to show only live
		if (this.rawData?.isLive !== true) {
			return null;
		}

		return this.rawData.isLive;
	}

	@computed get pageNumber() {
		return this.rawData?.pageNumber;
	}

	@computed get walletId() {
		return this.rootStore.userAuthStore.availableWallets?.find(
			(w) =>
				w.shopId == this.rawData.shopId &&
				w.abrv == this.rawData.bettingAccountTypeAbrv
		)?.id;
	}

	get activeTab() {
		return this._activeTab;
	}

	get defaultOpenBetsFilter() {
		const shopIdAndBettingTypeAbrv = this.getShopIdAndBettingTypeAbrv();
		const filter: rawFilter = {
			...createDefaultRawData(),
			betStatusAbrv: BetStatus.OPEN,
			period: BetPeriod.all,
			bettingAccountTypeAbrv:
				shopIdAndBettingTypeAbrv.bettingAccountTypeAbrv,
			shopId: shopIdAndBettingTypeAbrv.shopId,
		};

		return filter;
	}

	get defaultClosedBetsFilter() {
		const shopIdAndBettingTypeAbrv = this.getShopIdAndBettingTypeAbrv();
		const filter: rawFilter = {
			...createDefaultRawData(),
			betStatusAbrv: BetStatus.CLOSED,
			period: BetPeriod.today,
			bettingAccountTypeAbrv:
				shopIdAndBettingTypeAbrv.bettingAccountTypeAbrv,
			shopId: shopIdAndBettingTypeAbrv.shopId,
			fetchReport: true,
		};

		return filter;
	}
	// === ACTIONS ===

	setActiveTab = (tab: BetStatus) => {
		if (this.activeTab !== tab) {
			this._activeTab = tab;
		}
	};

	embed =
		"betSlipOffers,betSlipStatus,betStatus,betSlipType,betSlipSecureWinSetting";

	//#endregion filter components

	//#region setters

	@action.bound
	setAccountTypeIds(bettingAccountTypeAbrv: UserAccountType) {
		//when changing accountTypeIds, reset page number to 1
		const newFilter = { ...this.rawData, pageNumber: 1 };
		const previousBettingAccountTypeAbrv =
			this.rawData.bettingAccountTypeAbrv;
		newFilter.bettingAccountTypeAbrv = bettingAccountTypeAbrv;
		if (
			previousBettingAccountTypeAbrv ==
			UserAccountType.SportBettingAccountOnline
		) {
			newFilter.shopId =
				this.rootStore.userAuthStore.activeShopUserShopId;
		} else if (
			bettingAccountTypeAbrv ==
				UserAccountType.SportBettingAccountOnline &&
			(UserAccountType.SportBettingAccountShopOnline ==
				previousBettingAccountTypeAbrv ||
				UserAccountType.SportBettingAccountShop ==
					previousBettingAccountTypeAbrv)
		) {
			newFilter.shopId = this.rootStore.userAuthStore.defaultOnlineShopId;
		}
		this.setRawFilterData(newFilter);
	}

	@action.bound
	setBetStatusAbrv(betStatusAbrv: string) {
		this.rawData.betStatusAbrv = betStatusAbrv;
	}

	@action.bound
	setPeriod(period?: BetPeriod) {
		this.rawData.period = period;
	}

	@action.bound
	setIsLive(isLive: boolean) {
		this.rawData.isLive = isLive;
	}

	@action.bound
	setRawFilterData(filterData: rawFilter) {
		this.rawData = Object.assign(createDefaultRawData(), filterData);
		this.saveFilterToStorage(this.rawData);
		this.updateUrlParamsFromRawData();
		//set page number or reset to page 1
		this.setPageNumber(filterData.pageNumber || 1);
	}

	@action.bound
	setDefaultFilterData() {
		this.rawData = createDefaultRawData();
	}

	@action.bound
	setPageNumber(number: number) {
		this.rawData.pageNumber = number;
	}

	@action.bound
	setEventId(id: string) {
		this.eventId = id;
	}

	//#endregion setters

	@action.bound
	onInitialize() {
		const urlData = this.getUrlData();

		if (App.state.history.location.search !== "") {
			this.setRawFilterData(urlData);
		} else {
			const preservedFilter = this.getFilterFromStorage();
			this.setRawFilterData(this.defaultTabsFilter(this.activeTab));
			if (
				preservedFilter &&
				this.rootStore.userAuthStore.availableWallets?.some(
					(w) =>
						w.abrv == preservedFilter.bettingAccountTypeAbrv &&
						w.shopId == preservedFilter.shopId
				)
			) {
				this.rawData.bettingAccountTypeAbrv =
					preservedFilter.bettingAccountTypeAbrv;
				this.rawData.shopId = preservedFilter.shopId;
			}
		}
		this.isInitialized = true;
	}

	getUrlData() {
		return getUrlParamsAsObj<rawFilter>();
	}

	getShopIdAndBettingTypeAbrv() {
		return getShopIdAndBettingTypeAbrv({
			rawDataBettingAccountTypeAbrv: this.rawData.bettingAccountTypeAbrv,
			rawDataShopId: this.rawData.shopId,
			onlineAccountTypeAbrv: this.myBetsStore.onlineAccountType?.abrv,
			onlineShopAccountTypeAbrv:
				this.myBetsStore.onlineShopAccountType?.abrv,
			rootStore: this.rootStore,
		});
	}

	@action.bound
	updateUrlParamsFromRawData() {
		if (this.rawData.betStatusAbrv == BetStatus.OPEN) {
			redirectPreserveSearchParams(
				`/${getCurrentCulture()}/my-bets/open`,
				true
			);
		} else if (this.rawData.betStatusAbrv == BetStatus.CLOSED) {
			redirectPreserveSearchParams(
				`/${getCurrentCulture()}/my-bets/closed`,
				true
			);
		}

		const newUrlParams: FormFilter = {};
		for (const [key, value] of Array.from(Object.entries(this.rawData)) as [
			keyof FormFilter,
			any,
		][]) {
			if (value == null) {
				continue;
			}
			newUrlParams[key] = value;
		}

		updateUrlParams(newUrlParams, true);
	}

	saveFilterToStorage(filter: rawFilter) {
		if (filter.shopId == null || filter.bettingAccountTypeAbrv == null) {
			return;
		}

		const mappedFilter = {
			shopId: filter.shopId,
			bettingAccountTypeAbrv: filter.bettingAccountTypeAbrv,
		};

		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.MY_BETS_FILTER,
			JSON.stringify(mappedFilter)
		);
	}

	getFilterFromStorage() {
		const filter = safeJsonParse<{
			shopId: string;
			bettingAccountTypeAbrv: string;
		} | null>(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.MY_BETS_FILTER
			) as string,
			null
		);

		return filter;
	}

	defaultTabsFilter(tab: BetStatus): rawFilter {
		switch (tab) {
			case BetStatus.OPEN:
				return this.defaultOpenBetsFilter;
			case BetStatus.CLOSED:
				return this.defaultClosedBetsFilter;
			default:
				return this.defaultOpenBetsFilter;
		}
	}
	//#region disposers

	@action.bound
	onDispose() {
		this.onDisposeUrlParamsUpdated();
		this.setDefaultFilterData();
		this._activeTab = BetStatus.OPEN;
	}

	@action.bound
	onDisposeUrlParamsUpdated() {
		if (this.urlParamsUpdatedReaction != null) {
			this.urlParamsUpdatedReaction();
			this.urlParamsUpdatedReaction = null;
		}
	}

	//#endregion disposers
}
